import { useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import { ArrowRight, X } from '@phosphor-icons/react';
import { SECTION_IDS, site } from '../../content/site';
import { ThemeToggle } from './ThemeToggle';

type MobileMenuProps = {
	/** DOM id, referenced by the menu button's aria-controls. */
	id: string;
	open: boolean;
	activeId: string | null;
	onClose: () => void;
	/** Close the sheet, then scroll to the section once the page is unlocked. */
	onNavigate: (sectionId: string) => void;
};

const FOCUSABLE = 'a[href], button:not([disabled])';

/**
 * Full-screen menu sheet below 1024px (z-50, above the nav pill). Modal dialog:
 * focus moves to the close button, Tab is trapped, Escape closes. The top bar
 * mirrors the pill (same frame, same insets) so the pill seems to open in place.
 * Colors come from the nav tokens and --page-bg, so the sheet follows the chapter
 * (Mineral by day, Forest Night at dusk and in the dark theme). Reveals are CSS transitions, collapsed
 * to instant by the global reduced-motion rule.
 */
export function MobileMenu({ id, open, activeId, onClose, onNavigate }: MobileMenuProps) {
	const sheetRef = useRef<HTMLDivElement>(null);
	const closeRef = useRef<HTMLButtonElement>(null);

	// inert while closed (also during the fade-out), so nothing in it can be reached.
	useEffect(() => {
		const sheet = sheetRef.current;
		if (!sheet) return;
		if (open) sheet.removeAttribute('inert');
		else sheet.setAttribute('inert', '');
	}, [open]);

	useEffect(() => {
		const sheet = sheetRef.current;
		if (!open || !sheet) return;
		closeRef.current?.focus({ preventScroll: true });

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				onClose();
				return;
			}
			if (event.key !== 'Tab') return;
			const focusables = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE));
			if (!focusables.length) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const current = document.activeElement;
			if (event.shiftKey && (current === first || !sheet.contains(current))) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && (current === last || !sheet.contains(current))) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [open, onClose]);

	const go = (sectionId: string) => (event: MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		onNavigate(sectionId);
	};

	/** Staggered rise for the sheet content, in reading order. */
	const reveal = (index: number) => ({
		className: `transition-[transform,opacity] duration-700 ease-expo ${
			open ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
		}`,
		style: { transitionDelay: open ? `${120 + index * 60}ms` : '0ms' },
	});

	return (
		<div
			ref={sheetRef}
			id={id}
			role="dialog"
			aria-modal="true"
			aria-label={site.nav.menuLabel}
			data-lenis-prevent
			// Opening: visibility flips at once (focus can move in synchronously), opacity fades.
			// Closing: visibility is transitioned too, so it stays visible until the fade ends.
			className={`fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-[color:var(--page-bg)] text-[color:var(--nav-ink)] duration-500 ease-expo lg:hidden ${
				open ? 'visible opacity-100 transition-opacity' : 'invisible opacity-0 transition-[opacity,visibility]'
			}`}
		>
			<div className="flex min-h-full flex-col px-3 sm:px-4">
				<div className="mx-auto flex w-full max-w-[1180px] flex-1 flex-col">
					{/* Same frame as the pill: 12px top margin, 56px bar, 1px border, 20px / 6px insets. */}
					<div className="mt-3 flex h-14 items-center justify-between border border-transparent pl-5 pr-1.5">
						<a
							href={`#${SECTION_IDS.hero}`}
							onClick={go(SECTION_IDS.hero)}
							className="inline-flex h-11 items-center font-display text-[1.1875rem] font-bold tracking-[-0.02em]"
						>
							{site.person.name}
						</a>
						<div className="flex items-center gap-1">
							<ThemeToggle />
							<button
								ref={closeRef}
								type="button"
								onClick={onClose}
								aria-label={site.nav.menuClose}
								className="grid h-11 w-11 place-items-center rounded-full transition-colors duration-300 hover:bg-[color:var(--nav-border)]"
							>
								<X aria-hidden size={22} />
							</button>
						</div>
					</div>

					<div className="flex flex-1 flex-col px-[21px] pb-[max(2rem,env(safe-area-inset-bottom))] pt-[clamp(2.5rem,10vh,6rem)]">
						<ul className="grid gap-1">
							{site.nav.links.map((link, index) => {
								const current = activeId === link.id;
								const { className, style } = reveal(index);
								return (
									<li key={link.id} className={className} style={style}>
										<a
											href={`#${link.id}`}
											onClick={go(link.id)}
											aria-current={current ? 'true' : undefined}
											className={`block py-1.5 font-display text-[clamp(2.75rem,12vw,4.5rem)] font-bold leading-[1.02] tracking-[-0.035em] transition-colors duration-300 ${
												current ? 'text-[color:var(--nav-cta-bg)]' : ''
											}`}
										>
											{link.label}
										</a>
									</li>
								);
							})}
						</ul>

						<div
							className={`mt-auto pt-12 ${reveal(site.nav.links.length).className}`}
							style={reveal(site.nav.links.length).style}
						>
							<a
								href={`#${SECTION_IDS.contact}`}
								onClick={go(SECTION_IDS.contact)}
								className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[color:var(--nav-cta-bg)] px-8 text-base font-medium tracking-[-0.01em] text-[color:var(--nav-cta-ink)] transition-[background-color,transform] duration-300 ease-expo hover:bg-[color:var(--nav-cta-bg-hover)] active:scale-[0.98] sm:w-auto"
							>
								{site.nav.cta}
								<ArrowRight
									aria-hidden
									size={18}
									weight="bold"
									className="transition-transform duration-300 ease-expo group-hover:translate-x-0.5"
								/>
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
